import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Avatar, Box, Typography } from '@mui/material';
import { styled as styled } from '@mui/material/styles';
import isString from 'lodash/isString';
import { useDropzone } from 'react-dropzone';
import { fData } from 'src/utils/format-number';

// @mui
import { BlockContent } from './block-content';
import { RejectionFiles } from './rejection-files';
// ----------------------------------------------------------------------

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

const DropZoneStyle = styled('div')(({ theme }: any) => ({
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.primary.main}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ----------------------------------------------------------------------

export function UploadSingleFile({
  error = false,
  file,
  sx,
  onChange = (data: any): any => data,
  type,
  helperText,
  documentMode = false,
  // supportedFormats, // For Future reference
  ...other
}: any): JSX.Element {
  function onDropAccepted(files: any): any {
    onChange(files[0]);
  }

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    onDropAccepted,
    ...other,
  });
  const isImage =
    file?.type?.includes('image') || (type === 'image' && isString(file));
  const isVideo =
    file?.type?.includes('video') || (type === 'video' && isString(file));
  const isDocument =
    documentMode || type === 'document' || type === 'excel';
  const defaultHelperText = isDocument
    ? 'Click or drag Excel file here (.xlsx, .xls, max 10 MB)'
    : 'PNG or JPG no bigger than 10MB.';
  const placeholderText = helperText || defaultHelperText;

  return (
    <Box style={{ width: '100%', ...sx, mt: '20px' }}>
      <DropZoneStyle
        {...getRootProps()}
        style={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          display: 'flex',
          padding: '2%',
          flexDirection: 'row',
          alignItems: 'center',
          border: 'none',
          boxShadow: '0px 0px 1px 0px #0C1A4B3D',
        }}>
        <input {...getInputProps()} />
        {isImage ? (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Avatar
              src={isString(file) ? file : URL.createObjectURL(file)}
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                backgroundColor: '#00000',
                position: 'relative',
              }}>
              {!file && (
                <ImageIcon
                  style={{
                    color: 'text.secondary',
                    fontSize: '14px',
                    position: 'absolute',
                  }}
                />
              )}
            </Avatar>
            <Typography
              marginLeft="10px"
              width="100%"
              whiteSpace="nowrap"
              style={{ fontSize: 12 }}>
              Click or drag image here
            </Typography>
          </Box>
        ) : file && !isString(file) ? (
          <Box display="flex" flexDirection="row" alignItems="center" minWidth={0}>
            <Avatar style={{ backgroundColor: '#E8F0FE' }}>
              <DescriptionIcon style={{ color: '#1967D2', fontSize: 18 }} />
            </Avatar>
            <Box marginLeft="10px" minWidth={0}>
              <Typography
                variant="subtitle2"
                noWrap
                style={{ fontSize: 12, fontWeight: 600 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                {fData(file.size)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Avatar style={isDocument ? { backgroundColor: '#E8F0FE' } : undefined}>
              {isDocument ? (
                <DescriptionIcon style={{ color: '#1967D2', fontSize: 18 }} />
              ) : (
                <PersonAddIcon />
              )}
            </Avatar>
            <Typography
              marginLeft="10px"
              width="100%"
              color="text.secondary"
              style={{ fontSize: 12 }}>
              {placeholderText}
            </Typography>
          </Box>
        )}
        {isVideo && (
          <Box
            style={{
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              borderRadius: '.4rem',
              position: 'absolute',
              backgroundColor: 'background.paper',
            }}>
            <video
              width="100%"
              height="100%"
              controls
              aria-label="Uploaded video preview">
              <source src={URL.createObjectURL(file)} type={file?.type} />
              <track
                kind="captions"
                label="No captions available"
                src="data:text/vtt;charset=utf-8,WEBVTT%0A%0A"
                default
              />
            </video>
          </Box>
        )}
        {/* <BlockContent supportedFormats={supportedFormats} file={file} /> For Future use */}
        <BlockContent />
      </DropZoneStyle>

      {fileRejections.length > 0 && (
        <RejectionFiles fileRejections={fileRejections} />
      )}
    </Box>
  );
}
